import type { Template } from '../types';

interface TemplateFieldsProps {
  template: Template | null;
  fields: Record<string, string>;
  onChange: (fieldName: string, value: string) => void;
  /** Optional prefix for input ids (to avoid collisions when multiple forms on a page). */
  idPrefix?: string;
}

/**
 * Renders the standard template fields: Language, Format, Audience Level, and Tags.
 * Order matches the Administration settings (Languages → Formats → Audience levels → Tags).
 * Each field only renders when its options array has ≥1 item.
 */
export function TemplateFields({ template, fields, onChange, idPrefix = '' }: TemplateFieldsProps) {
  if (!template) return null;

  const languageOptions = template.languageOptions ?? [];
  const formatOptions = template.formatOptions ?? [];
  const levelOptions = template.levelOptions ?? [];
  const tagOptions = template.tagOptions ?? [];

  const tagsValue = (fields['tags'] || '') as string;
  const selectedTags = tagsValue ? tagsValue.split(',').map((s) => s.trim()).filter(Boolean) : [];

  return (
    <>
      {/* Language */}
      {languageOptions.length > 0 && (
        <div>
          <label htmlFor={`${idPrefix}language`} className="label">Language</label>
          <select
            id={`${idPrefix}language`}
            className="select"
            value={fields['language'] || ''}
            onChange={(e) => onChange('language', e.target.value)}
          >
            <option value="">Select...</option>
            {languageOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      )}

      {/* Format */}
      {formatOptions.length > 0 && (
        <div>
          <label htmlFor={`${idPrefix}format`} className="label">Format</label>
          <select
            id={`${idPrefix}format`}
            className="select"
            value={fields['format'] || ''}
            onChange={(e) => onChange('format', e.target.value)}
          >
            <option value="">Select...</option>
            {formatOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      )}

      {/* Audience Level */}
      {levelOptions.length > 0 && (
        <div>
          <label htmlFor={`${idPrefix}level`} className="label">Audience Level</label>
          <select
            id={`${idPrefix}level`}
            className="select"
            value={fields['level'] || ''}
            onChange={(e) => onChange('level', e.target.value)}
          >
            <option value="">Select...</option>
            {levelOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      )}

      {/* Tags (multi-select checkboxes) */}
      {tagOptions.length > 0 && (
        <div>
          <label className="label">Tags</label>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Tags">
            {tagOptions.map((tag) => (
              <label key={tag} className="inline-flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedTags.includes(tag)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...selectedTags, tag]
                      : selectedTags.filter((t) => t !== tag);
                    onChange('tags', next.join(', '));
                  }}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">{tag}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
